import React, { useState, useEffect, useCallback } from 'react';
import { accountsApi, categoriesApi, transactionsApi } from '../services/api';
import { fmt, monthLabel } from '../utils/format';
import {
  computeEmergencyFund, computeInvestedLiquidTotal, accountBalance, accountLedgerBalance,
  isLiabilityAccount,
} from '../utils/calculations';
import Modal from '../components/Modal';
import { useUnsavedFlag } from '../context/UnsavedChanges';

const CC_NAMES     = new Set(['Amex', 'Medical CC']);
const LIQUID_NAMES = ['Cash', 'Other Liquid Assets'];

// ─────────────────────────────────────────────────────────────────────────────
// Row pieces live at module scope, NOT inside AccountsTab.
//
// A component declared inside another component is a brand-new type on every
// render, so React unmounts the old one and mounts a fresh one instead of
// updating it — which throws away focus and the caret. That's why typing a
// balance only ever registered one character before the cursor jumped out.
// StrategyTab's InlineNum already carries this warning; these didn't.
// ─────────────────────────────────────────────────────────────────────────────

const PILL = {
  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
  cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em',
  transition: 'all 0.15s',
};

/** Pill toggle for isInvestedLiquid. */
function ILToggle({ acct, onToggle }) {
  const on = acct.isInvestedLiquid;
  return (
    <button onClick={() => onToggle(acct)}
      title={on ? 'Remove from invested liquid' : 'Mark as invested liquid'}
      style={{ ...PILL,
        border: `1px solid ${on ? 'var(--green)' : 'var(--border2)'}`,
        background: on ? 'var(--green-bg)' : 'transparent',
        color: on ? 'var(--green)' : 'var(--text3)' }}>
      {on ? '⚡ Liquid' : '+ Liquid'}
    </button>
  );
}

/** Pill toggle for ledgerDriven — every account can be kept by hand instead. */
function LDToggle({ acct, onToggle }) {
  const on = acct.ledgerDriven;
  return (
    <button onClick={() => onToggle(acct)}
      title={on ? 'Switch to manual balance entry' : 'Update automatically from transactions'}
      style={{ ...PILL,
        border: `1px solid ${on ? 'var(--blue)' : 'var(--border2)'}`,
        background: on ? 'var(--blue-bg)' : 'transparent',
        color: on ? 'var(--blue)' : 'var(--text3)' }}>
      {on ? 'Live' : 'Manual'}
    </button>
  );
}

/**
 * The balance cell for any account row, in either mode:
 *  · Live   — computed from the ledger, read-only (typing over it would just
 *             drift from the transactions that produce it)
 *  · Manual — an editable input, saved via the shared dirty/Save-all flow
 *
 * `abs` displays the magnitude (credit cards and loans are stored negative);
 * `negate` writes edits back as negative, so a loan typed as 500 stores -500.
 */
function BalanceCell({ acct, txs, value, onChange, abs = false, negate = false }) {
  if (acct.ledgerDriven) {
    const v = accountBalance(acct, txs);
    return (
      <span className={v < 0 ? 'neg' : 'pos'}
        style={{ fontSize: 14, fontWeight: 600, minWidth: 100, textAlign: 'right' }}>
        {fmt(abs ? Math.abs(v) : v)}
      </span>
    );
  }
  const raw = parseFloat(value) || 0;
  return (
    <input className="field-input" type="number" step="0.01"
      style={{ width: 120, textAlign: 'right', fontSize: 13, padding: '4px 8px' }}
      value={abs || negate ? Math.abs(raw) : value}
      onChange={e => {
        const n = parseFloat(e.target.value) || 0;
        onChange(negate ? -Math.abs(n) : e.target.value);
      }} />
  );
}

/** Click-to-rename account name — saves on blur/Enter. */
function AccountName({ acct, onRename }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(acct.name);
  useEffect(() => { setVal(acct.name); }, [acct.name]);
  if (!editing) {
    return (
      <span onClick={() => setEditing(true)} title="Click to rename"
        style={{ fontSize: 13, cursor: 'pointer', borderBottom: '1px dashed var(--border2)' }}>
        {acct.name}
      </span>
    );
  }
  return (
    <input className="field-input" autoFocus
      style={{ fontSize: 13, padding: '2px 6px', width: 140 }}
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={() => { setEditing(false); onRename(acct, val); }}
      onKeyDown={e => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') { setVal(acct.name); setEditing(false); }
      }} />
  );
}

export default function AccountsTab({ month, onRefresh }) {
  const [accounts,   setAccounts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [txs,        setTxs]        = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [dirty,      setDirty]      = useState({});       // id → balance string
  const [liquidVals, setLiquidVals] = useState({});
  const [showAdd,    setShowAdd]    = useState(false);
  const [newAcct,    setNewAcct]    = useState({
    name: '', accountType: 'Budget', balance: '',
    isCreditCard: false, isInvestedLiquid: false, ledgerDriven: true,
  });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      accountsApi.getAll(month.id),
      categoriesApi.getAll(month.id),
      transactionsApi.getAll(month.id),
    ]).then(([aR, cR, tR]) => {
      setAccounts(aR.data);
      setCategories(cR.data);
      setTxs(tR.data);
      const lv = {};
      LIQUID_NAMES.forEach(name => {
        const found = aR.data.find(a => a.name === name);
        lv[name] = found ? found.balance : 0;
      });
      setLiquidVals(lv);
      setDirty({});
      setLoading(false);
    });
  }, [month.id]);

  useEffect(() => { load(); }, [load]);

  const txBalance = useCallback((name) => {
    const raw = txs.filter(t => t.accountName === name)
      .reduce((s, t) => s + (t.type === 'Deposit' ? t.amount : -t.amount), 0);
    return Math.round(raw * 100) / 100 || 0;
  }, [txs]);

  const getVal = (acct) =>
    dirty[acct.id] !== undefined
      ? dirty[acct.id]
      : parseFloat((acct.balance || 0).toFixed(2));

  // Toggle isInvestedLiquid directly and save immediately
  const handleToggleInvestedLiquid = async (acct) => {
    const next = !acct.isInvestedLiquid;
    // Optimistic update
    setAccounts(prev => prev.map(a => a.id === acct.id ? { ...a, isInvestedLiquid: next } : a));
    await accountsApi.update(acct.id, { isInvestedLiquid: next });
    load(); onRefresh();
  };

  // Toggle whether an account's balance is live-computed from the ledger
  // vs. manually entered.
  const handleToggleLedgerDriven = async (acct) => {
    const next = !acct.ledgerDriven;
    // The number on screen must not change just because the way it's derived
    // did. Going Live, back out this month's movement so opening + movement
    // lands on the balance already shown; going Manual, store whatever the
    // ledger was displaying.
    const shown    = accountBalance(acct, txs);
    const movement = accountLedgerBalance(txs, acct.name);
    let opening    = Math.round((shown - movement) * 100) / 100;
    // Same rule as month carry / Up for Grabs: a leftover on a loan or card
    // is not an assignable opening.
    if (next && isLiabilityAccount(acct, categories) && opening > 0) opening = 0;
    const payload = next
      ? { ledgerDriven: true,  openingBalance: opening, balance: shown }
      : { ledgerDriven: false, balance: shown };

    setAccounts(prev => prev.map(a => a.id === acct.id ? { ...a, ...payload } : a));
    await accountsApi.update(acct.id, payload);
    setDirty(d => { const { [acct.id]: _, ...rest } = d; return rest; });
    load(); onRefresh();
  };

  // Inline rename — saves on blur/Enter, independent of the balance dirty/Save-all flow.
  const handleRename = async (acct, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed || trimmed === acct.name) return;
    setAccounts(prev => prev.map(a => a.id === acct.id ? { ...a, name: trimmed } : a));
    await accountsApi.update(acct.id, { name: trimmed });
    load(); onRefresh();
  };

  const handleSaveAll = async () => {
    await Promise.all(Object.entries(dirty).map(([id, bal]) =>
      accountsApi.update(Number(id), { balance: parseFloat(bal) || 0 })
    ));
    await Promise.all(LIQUID_NAMES.map(async (name) => {
      const existing = accounts.find(a => a.name === name);
      const balance  = parseFloat(liquidVals[name]) || 0;
      if (existing) {
        if (existing.balance !== balance)
          await accountsApi.update(existing.id, { balance });
      } else if (balance !== 0) {
        await accountsApi.add(month.id, { name, accountType: 'Budget', balance, isCreditCard: false });
      }
    }));
    setDirty({}); load(); onRefresh();
  };

  const handleDelete = async (acct) => {
    // Say what actually happens — the delete reaches forward and takes the
    // account's ledger rows with it.
    const ok = window.confirm(
      `Remove "${acct.name}" from ${monthLabel(month)} and every later month?\n\n`
      + `Its transactions in those months are deleted too. Earlier months keep both.`);
    if (!ok) return;
    await accountsApi.delete(acct.id); load(); onRefresh();
  };

  const handleAdd = async () => {
    if (!newAcct.name.trim()) return;
    const name = newAcct.name.trim();
    // Liabilities are stored negative regardless of how the balance was typed —
    // a loan or card entered as 500 means "I owe 500". A positive one hides the
    // debt from the Strategy tab and inflates net worth.
    const rawBalance = parseFloat(newAcct.balance) || 0;
    const owed = newAcct.accountType === 'Loan' || newAcct.isCreditCard;
    const balance = owed ? -Math.abs(rawBalance) : rawBalance;
    const live = newAcct.accountType === 'Budget' && newAcct.ledgerDriven;

    // The server records the Starting balance entry for a Live account and
    // copies the account into later months, so this is one call rather than a
    // sequence the browser could half-complete.
    await accountsApi.add(month.id, {
      name, accountType: newAcct.accountType,
      balance,
      isCreditCard: newAcct.isCreditCard,
      isInvestedLiquid: newAcct.isInvestedLiquid,
      ledgerDriven: live,
    });

    // Credit-card accounts get their own budget category for payments / starting balance.
    if (newAcct.isCreditCard && !categories.some(c => c.name === name))
      await categoriesApi.add(month.id, { groupName: 'Credit Card', name, planned: 0, assigned: 0 });
    setNewAcct({ name: '', accountType: 'Budget', balance: '', isCreditCard: false, isInvestedLiquid: false, ledgerDriven: true });
    setShowAdd(false); load();
  };

  // ── Grouping ───────────────────────────────────────────────────────────────
  const creditCardAccts    = accounts.filter(a => CC_NAMES.has(a.name) || a.isCreditCard);
  const ccIds              = new Set(creditCardAccts.map(a => a.id));
  const investedLiquidAccts = accounts.filter(a => a.isInvestedLiquid && !ccIds.has(a.id));
  const ilIds              = new Set(investedLiquidAccts.map(a => a.id));

  const byType        = (type) => accounts.filter(a => a.accountType === type && !ccIds.has(a.id) && !ilIds.has(a.id));
  const budgetAccts   = byType('Budget');
  const loanAccts     = byType('Loan');
  const trackingAccts = byType('Tracking');
  const assetAccts    = byType('Asset');
  const otherBudget   = budgetAccts.filter(a => !LIQUID_NAMES.includes(a.name));

  // Whatever a row displays is what the subtotals must sum — otherwise a Manual
  // account shows one number and the subtotal silently uses another.
  const effBalance = (a) => accountBalance(a, txs);

  const liquidTotal       = LIQUID_NAMES.reduce((s, name) => s + (parseFloat(liquidVals[name]) || 0), 0);
  const ccTotal           = creditCardAccts.reduce((s, a) => s + effBalance(a), 0);
  const investedLiqTotal  = computeInvestedLiquidTotal(accounts, txs);
  const budgetTotal       = otherBudget.reduce((s, a) => s + effBalance(a), 0) + liquidTotal;
  const loanTotal     = loanAccts.reduce((s, a) => s - Math.abs(effBalance(a)), 0);
  const trackingTotal = trackingAccts.reduce((s, a) => s + effBalance(a), 0);
  const assetTotal    = assetAccts.reduce((s, a) => s + effBalance(a), 0);
  const netWorth      = budgetTotal + ccTotal + investedLiqTotal + loanTotal + trackingTotal + assetTotal;
  const emergencyTotal = computeEmergencyFund(categories, accounts, txs);

  const hasDirty = Object.keys(dirty).length > 0 ||
    LIQUID_NAMES.some(name => {
      const existing = accounts.find(a => a.name === name);
      return parseFloat(liquidVals[name]) !== (existing ? existing.balance : 0);
    });

  // Typing a balance without hitting Save used to be discarded silently on
  // tab/month change — now navigating away asks first.
  useUnsavedFlag('Accounts', hasDirty);

  if (loading) return <div className="spinner" />;

  const rowStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 0', borderBottom: '1px solid var(--border)',
  };

  return (
    <>
      {/* Metric grid */}
      <div className="metric-grid">
        <div className="metric-box"><div className="label">Budget</div><div className={`value ${budgetTotal >= 0 ? 'pos' : 'neg'}`}>{fmt(budgetTotal)}</div></div>
        <div className="metric-box"><div className="label">Credit Cards</div><div className={`value ${ccTotal >= 0 ? 'pos' : 'neg'}`}>{fmt(ccTotal)}</div></div>
        <div className="metric-box"><div className="label">Investments</div><div className="value pos">{fmt(trackingTotal)}</div></div>
        <div className="metric-box"><div className="label">Loans</div><div className="value neg">{fmt(loanTotal)}</div></div>
        <div className="metric-box"><div className="label">Assets</div><div className="value pos">{fmt(assetTotal)}</div></div>
        <div className="metric-box" style={{ border: '1px solid var(--green)', background: 'var(--green-bg)' }}>
          <div className="label" style={{ color: 'var(--green)' }}>Emergency Fund</div>
          <div className="value pos">{fmt(emergencyTotal)}</div>
          <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 2, opacity: 0.8 }}>
            Cash + savings + brokerage — not envelope Available
          </div>
        </div>
        <div className="metric-box" style={{ border: '2px solid var(--border2)' }}>
          <div className="label">Net Worth</div>
          <div className={`value ${netWorth >= 0 ? 'pos' : 'neg'}`}>{fmt(netWorth)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <button
          className={`btn${hasDirty ? ' btn-primary' : ''}`}
          onClick={handleSaveAll}
          disabled={!hasDirty}
          style={{ minWidth: 100, opacity: hasDirty ? 1 : 0.4 }}
        >
          {hasDirty ? 'Save *' : 'Saved'}
        </button>
        <button className="btn" onClick={() => setShowAdd(true)}>+ Account</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(310px,1fr))', gap: 16, marginBottom: 16 }}>

        {/* Budget */}
        <div className="card" style={{ margin: 0 }}>
          <div className="sec-label">Budget</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            Transaction-driven accounts update automatically
          </div>
          {otherBudget.map(a => (
            <div key={a.id} style={rowStyle}>
              <AccountName acct={a} onRename={handleRename} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BalanceCell acct={a} txs={txs} value={getVal(a)} onChange={v => setDirty(d => ({ ...d, [a.id]: v }))} />
                <LDToggle acct={a} onToggle={handleToggleLedgerDriven} />
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>✕</button>
              </div>
            </div>
          ))}

          {/* Liquid sub-group */}
          <div style={{ margin: '12px 0 4px', padding: '10px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Liquid</div>
            <div style={{ fontSize: 11, color: 'var(--blue)', marginBottom: 10 }}>Feeds Up for Grabs</div>
            {LIQUID_NAMES.map(name => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13 }}>{name}</span>
                <input className="field-input" type="number" step="0.01"
                  style={{ width: 120, textAlign: 'right', fontSize: 13, padding: '4px 8px' }}
                  value={liquidVals[name] ?? 0}
                  onChange={e => setLiquidVals(prev => ({ ...prev, [name]: e.target.value }))} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontWeight: 700, fontSize: 14, paddingTop: 8, borderTop: '2px solid var(--border2)' }}>
              <span>Total</span>
              <span className={liquidTotal < 0 ? 'neg' : 'pos'}>{fmt(liquidTotal)}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontWeight: 700, fontSize: 13, borderTop: '2px solid var(--border2)', paddingTop: 10 }}>
            <span>Subtotal</span>
            <span className={budgetTotal < 0 ? 'neg' : 'pos'}>{fmt(budgetTotal)}</span>
          </div>
        </div>

        {/* Credit Cards */}
        {creditCardAccts.length > 0 && (
          <div className="card" style={{ margin: 0 }}>
            <div className="sec-label">Credit Cards</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
              Balances come from the ledger. Switch a card to Manual if you'd rather keep its
              balance by hand.
            </div>
            {creditCardAccts.map(a => (
              <div key={a.id} style={rowStyle}>
                <AccountName acct={a} onRename={handleRename} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BalanceCell acct={a} txs={txs} value={getVal(a)} onChange={v => setDirty(d => ({ ...d, [a.id]: v }))} abs negate />
                  <LDToggle acct={a} onToggle={handleToggleLedgerDriven} />
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>✕</button>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontWeight: 700, fontSize: 13, borderTop: '2px solid var(--border2)', paddingTop: 10 }}>
              <span>Subtotal</span><span className="pos">{fmt(Math.abs(ccTotal))}</span>
            </div>
          </div>
        )}

        {/* Invested Liquid */}
        <div className="card" style={{ margin: 0, border: '1px solid var(--green)' }}>
          <div className="sec-label" style={{ color: 'var(--green)' }}>Invested Liquid</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 10 }}>
            Investments that can be quickly liquidated in an emergency — counted toward Emergency Fund.
            Set an account to Live to value it from the transaction ledger, or Manual to enter the
            balance yourself. Toggle any investment account below to add or remove it.
          </div>

          {investedLiquidAccts.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0', marginBottom: 8 }}>
              No accounts marked yet. Use the ⚡ toggle on any investment account.
            </div>
          )}

          {investedLiquidAccts.map(a => (
            <div key={a.id} style={rowStyle}>
              <div>
                <AccountName acct={a} onRename={handleRename} />
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{a.accountType}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BalanceCell acct={a} txs={txs} value={getVal(a)} onChange={v => setDirty(d => ({ ...d, [a.id]: v }))} />
                <LDToggle acct={a} onToggle={handleToggleLedgerDriven} />
                <ILToggle acct={a} onToggle={handleToggleInvestedLiquid} />
              </div>
            </div>
          ))}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontWeight: 700, fontSize: 13, borderTop: '2px solid var(--green)', paddingTop: 10 }}>
            <span>Emergency Fund total</span>
            <span className="pos">{fmt(emergencyTotal)}</span>
          </div>
        </div>

        {/* Loans */}
        {loanAccts.length > 0 && (
          <div className="card" style={{ margin: 0 }}>
            <div className="sec-label">Loans</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Update monthly from statements, or switch to Live to track from the ledger</div>
            {loanAccts.map(a => (
              <div key={a.id} style={rowStyle}>
                <AccountName acct={a} onRename={handleRename} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BalanceCell acct={a} txs={txs} value={getVal(a)} onChange={v => setDirty(d => ({ ...d, [a.id]: v }))} abs negate />
                  <LDToggle acct={a} onToggle={handleToggleLedgerDriven} />
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>✕</button>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontWeight: 700, fontSize: 13, borderTop: '2px solid var(--border2)', paddingTop: 10 }}>
              <span>Subtotal</span><span className="pos">{fmt(Math.abs(loanTotal))}</span>
            </div>
          </div>
        )}

        {/* Investments — with ⚡ toggle on each row */}
        {trackingAccts.length > 0 && (
          <div className="card" style={{ margin: 0 }}>
            <div className="sec-label">Investments</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
              Update monthly · Toggle ⚡ to mark an account as invested liquid (emergency fund)
            </div>
            {trackingAccts.map(a => (
              <div key={a.id} style={rowStyle}>
                <AccountName acct={a} onRename={handleRename} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BalanceCell acct={a} txs={txs} value={getVal(a)} onChange={v => setDirty(d => ({ ...d, [a.id]: v }))} />
                  <LDToggle acct={a} onToggle={handleToggleLedgerDriven} />
                  <ILToggle acct={a} onToggle={handleToggleInvestedLiquid} />
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>✕</button>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontWeight: 700, fontSize: 13, borderTop: '2px solid var(--border2)', paddingTop: 10 }}>
              <span>Subtotal</span><span className="pos">{fmt(trackingTotal)}</span>
            </div>
          </div>
        )}

        {/* Assets */}
        {assetAccts.length > 0 && (
          <div className="card" style={{ margin: 0 }}>
            <div className="sec-label">Assets</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Home value, gold, etc.</div>
            {assetAccts.map(a => (
              <div key={a.id} style={rowStyle}>
                <AccountName acct={a} onRename={handleRename} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BalanceCell acct={a} txs={txs} value={getVal(a)} onChange={v => setDirty(d => ({ ...d, [a.id]: v }))} />
                  <LDToggle acct={a} onToggle={handleToggleLedgerDriven} />
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>✕</button>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontWeight: 700, fontSize: 13, borderTop: '2px solid var(--border2)', paddingTop: 10 }}>
              <span>Subtotal</span><span className="pos">{fmt(assetTotal)}</span>
            </div>
          </div>
        )}
      </div>


      {showAdd && (
        <Modal title="Add account" onClose={() => setShowAdd(false)}>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Name</label>
            <input className="field-input" style={{ width: '100%' }} value={newAcct.name}
              onChange={e => setNewAcct(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Type</label>
            <select className="field-input" style={{ width: '100%' }} value={newAcct.accountType}
              onChange={e => setNewAcct(p => ({ ...p, accountType: e.target.value }))}>
              <option>Budget</option><option>Loan</option><option>Tracking</option><option>Asset</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Starting balance</label>
            <input className="field-input" type="number" step="0.01" style={{ width: '100%' }}
              value={newAcct.balance} onChange={e => setNewAcct(p => ({ ...p, balance: e.target.value }))} />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.45 }}>
              {(newAcct.accountType === 'Loan' || newAcct.isCreditCard)
                ? 'Enter what you owe as a positive number — it\'s stored as a debt.'
                : 'What the account holds today.'}
              {newAcct.accountType === 'Budget' && newAcct.ledgerDriven && (parseFloat(newAcct.balance) || 0) !== 0 && (
                <> Recorded as a <strong>Starting balance</strong> entry in the Ledger, dated the 1st.</>
              )}
            </div>
          </div>
          {newAcct.accountType === 'Budget' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13 }}>
              <input type="checkbox" checked={newAcct.ledgerDriven}
                onChange={e => setNewAcct(p => ({ ...p, ledgerDriven: e.target.checked }))} />
              Update automatically from transactions
            </label>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13 }}>
            <input type="checkbox" checked={newAcct.isCreditCard}
              onChange={e => setNewAcct(p => ({ ...p, isCreditCard: e.target.checked }))} />
            Credit card
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13 }}>
            <input type="checkbox" checked={newAcct.isInvestedLiquid}
              onChange={e => setNewAcct(p => ({ ...p, isInvestedLiquid: e.target.checked }))} />
            Invested liquid (counts toward Emergency Fund)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleAdd}>Add</button>
            <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </Modal>
      )}
    </>
  );
}
